import { Service } from '@feathersjs/feathers';
import { Paginated } from "@feathersjs/feathers/lib";
import axios, { AxiosRequestConfig } from 'axios';
import { makeObservable, observable, runInAction } from "mobx";
import { DataType, IData, IStep, Services } from "types";
import client from "../feathers";

import { Data } from ".";

interface IFile extends IData {
  uri:           string | ArrayBuffer;
  mime_type:     string;
  key?:          string;
  title?:        string;
  course_id?:    number;
  step_id?:      number;
  size?:         number;
  is_thumbnail?: boolean;
}


export class File extends Data<IFile> implements IFile {

  course_id:    number = null;
  title:        string = null;
  mime_type:    string = null;
  step_id:      number = null;
  key:          string = null;
  size:         number = null;
  is_thumbnail: boolean = false;
  uri:          string | ArrayBuffer = null; // URI to 
  file_path?:   string = null;


  progress  = 0;      // Up-/downloadload progress
  uploading = false;


  protected storage_service: Service<{ url: string }>;


  constructor(data: IFile) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.File, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually
    makeObservable(this, {
      uri:          observable,
      file_path:    observable,
      title:        observable,
      key:          observable,
      size:         observable,
      mime_type:    observable,
      course_id:    observable,
      step_id:      observable,
      is_thumbnail: observable,
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here.
    this.fromData(data);
    this.storage_service = client.service(Services.Storage);
  }
  /*******************************************************************************************************************************************
   * Database Access
   ********************************************************************************************************************************************/

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/

  public static get = async (id?: number): Promise<File | File[]> => {
    try {
      if (id) {
        return File.create(await client.service(Services.Document).get(id));
      } else {
        const files: Paginated<IFile> = await client.service(Services.Document).find();
        if (!files.total) return [];
        return files.data.map(f => File.create(f));
      }
    } catch (err) {
      console.error("Failed to load Files", err);
      return null;
    }
  }

  public static create(data: IFile): File {
    return new File(data);
  }

  protected fromData(data: IFile): void {
    super.fromData(data);
    this.uri          = data.uri && data.uri;
    this.title        = data.title && data.title;
    this.mime_type    = data.mime_type && data.mime_type;
    this.course_id    = data.course_id && Number(data.course_id);
    this.step_id      = data.step_id && Number(data.step_id);
    this.key          = data.key && data.key;
    this.size         = data.size && Number(data.size);
    this.is_thumbnail = Boolean(data.is_thumbnail);
  }

  public toData(): any {
    return {
      ...super.toData(),
      course_id:    this.course_id || null,
      step_id:      this.step_id || null,
      title:        this.title || null,
      mime_type:    this.mime_type || null,
      size:         this.size || null,
      key:          this.key || null,
      is_thumbnail: this.is_thumbnail || null,
    };
  }

  public toPatchData(patch: Partial<IStep>): any {
    return {
      ...super.toPatchData(patch),
      course_id:    "course_id" in patch ? this.course_id:       undefined,
      step_id:      "step_id" in patch ? this.step_id:           undefined,
      uri:          "uri" in patch ? this.uri:                   undefined,
      key:          "key" in patch ? this.key:                   undefined,
      size:         "size" in patch ? this.size:                 undefined,
      title:        "title" in patch ? this.title:               undefined,
      mime_type:    "mime_type" in patch ? this.mime_type:       undefined,
      is_thumbnail: "is_thumbnail" in patch ? this.is_thumbnail: undefined,
    };
  }

  /*******************************************************************************************************************************************
   * Getter
   ********************************************************************************************************************************************/

  public get canEdit(): boolean {
    return true;
  }

  /*******************************************************************************************************************************************
   * Setter
   ********************************************************************************************************************************************/

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/

  public async upload(storeInDatabase = true): Promise<void> {
    // Upload represented file to storage
    try {
      // Get signed S3 URL
    
      const { url, key, created_at, acl } = await this.storage_service.create(this.toData()) as any;

      if (!(url && key && created_at)) {
        console.error("Failed to created signed URL.");
        return;
      }

      this.key = key;
      this.created_at = new Date(created_at);

      // PUT the file to the signed S3 URL
      const options: AxiosRequestConfig = {
        headers: {
          'Content-Type': this.mime_type,
          'x-amz-acl': acl
        },
        onUploadProgress: (progressEvent) => this.progress = Math.round((progressEvent.loaded / progressEvent.total) * 100)
      };

      this.uploading = true;
      const b4 = await fetch(`${this.uri}`).then(res => res.blob()); // TODO CHECK FILE TYPE
      await axios.put(url, b4, options);
      this.uploading = false;

    } catch (error) {
      console.error('Error while uploading file to storage', error);
    }

    if (!this.key) {
      console.error("Failed to upload file.")
      return;
    }

    if (storeInDatabase) {
      await super.save(); // Save to document table
      if (!this.id) {
        console.error("Failed to create database object. Rolling back");
        try {
          this.storage_service.remove(this.key);
          this.key = null;
        } catch (err) {
          console.error("Failed clean up file. Potentially orphaned data with key ", this.key);
        }
      }
    }

    return;
  }

  public getURL = async (): Promise<string> => {
    const res: { url: string } = await this.storage_service.get(this.key);
    runInAction(() => this.file_path = res.url);
    return res.url;
  }

  public openInWindow = async (): Promise<void> => {
    const url = await this.getURL();
    window.open(url, '_blank').focus();
  }
}
